import React, { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { changePassword } from "../../Actions/AuthActions";
import {
    Box,
    Grid,
    TextField,
    Typography,
    Dialog,
    DialogContent,
    IconButton,
    InputAdornment,
} from "@mui/material";
import { KeyRound } from "lucide-react";
import CustomSnackbar from "../Common/snackBar";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { MyCloseIcon, MyInfoIcon, MyVisibilityIcon, MyVisibilityOffIcon } from "../../Assets/svg/MyIcons";
import PasswordStrengthRules, { isPasswordValid } from '../../components/Common/PasswordStrengthRules';
import { FRONTEND_MESSAGES } from "../../utils/messages";
import "./ChangeProfile.css";

const { VALIDATION } = FRONTEND_MESSAGES;

const validationSchema = yup.object().shape({
    oldPassword: yup.string().required(VALIDATION.REQUIRED_FIELD("Old password")),
    newPassword: yup
        .string()
        .required(VALIDATION.REQUIRED_FIELD("New password"))
        .min(8, VALIDATION.MIN_LENGTH("Password", 8))
        .max(20, VALIDATION.MAX_LENGTH("Password", 20))
        .matches(/[A-Z]/, VALIDATION.PASSWORD_UPPERCASE)
        .matches(/[a-z]/, VALIDATION.PASSWORD_LOWERCASE)
        .matches(/[0-9]/, VALIDATION.PASSWORD_NUMBER)
        .matches(/[!"#$%&'()*+,\-./:;<=>?@\[\\\]\^_`{|}~]/, VALIDATION.PASSWORD_SPECIAL)
        .test("no-spaces", VALIDATION.NO_SPACES("Password"), (value) => !/\s/.test(value)),
    confirmNewPassword: yup
        .string()
        .required(VALIDATION.REQUIRED_FIELD("Confirm new password"))
        .oneOf([yup.ref("newPassword"), null], VALIDATION.PASSWORD_MISMATCH),
});

const ChangePasswordComponent = ({ open, onClose, snackbar, setSnackbar }) => {
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
    const [newPasswordFocused, setNewPasswordFocused] = useState(false);
    const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);

    const [buttonDisable, setButtonDisable] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleToggleOldPasswordVisibility = () => {
        setShowOldPassword((prevShowPassword) => !prevShowPassword);
    };

    const handleToggleNewPasswordVisibility = () => {
        setShowNewPassword((prevShowPassword) => !prevShowPassword);
    };

    const handleToggleConfirmNewPasswordVisibility = () => {
        setShowConfirmNewPassword((prevShowPassword) => !prevShowPassword);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const formik = useFormik({
        initialValues: {
            oldPassword: "",
            newPassword: "",
            confirmNewPassword: "",
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            // if (!isPasswordValid(values.newPassword)) {
            //     setSnackbar({
            //         open: true,
            //         message: "Password does not meet all requirements",
            //         severity: "error",
            //     });
            //     return;
            // }
            setButtonDisable(true);
            setLoading(true);
            await changePassword(values)
                .then((res) => {
                    if (res.status === "202") {
                        setSnackbar({
                            open: true,
                            message: res.message,
                            severity: "info",
                        });
                    } else {
                        setSnackbar({
                            open: true,
                            message: res.data.message,
                            severity: "success",
                        });
                        setTimeout(() => { onClose(); }, 3000);
                        // localStorage.removeItem("token");
                        // localStorage.removeItem("userData");
                        // setTimeout(() => {
                        //   navigate("/login");
                        // }, 1500);
                    }
                })
                .catch((error) => {
                    setSnackbar({
                        open: true,
                        message: error.response?.data?.message,
                        severity: "error",
                    });
                });
            // formik.resetForm();
            setLoading(false);
            setButtonDisable(false);
        },
    });

    const handlePasswordChange = (e) => {
        const value = e.target.value.replace(/\s/g, '');
        formik.setFieldValue(e.target.name, value);
        formik.setFieldTouched(e.target.name, true, false);
    };

    const HtmlTooltip = styled(({ className, ...props }) => (
        <Tooltip {...props} classes={{ popper: className }} arrow />
    ))(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: "#f5f5f9",
            color: "rgba(0, 0, 0, 0.87)",
            minWidth: 600,
            fontSize: "14px",
            border: "1px solid #dadde9",
        },
    }));

    const handleCloseModal = () => {
        formik.resetForm(); // Reset the Formik form (this clears any validation errors)
        setShowOldPassword(false); // Reset password visibility state
        setShowNewPassword(false); // Reset password visibility state
        setShowConfirmNewPassword(false); // Reset password visibility state
        onClose(); // Close the modal
    };

    return (
        <Dialog
            open={open}
            onClose={handleCloseModal}
            maxWidth="sm"
            fullWidth
            PaperProps={{ className: "pf-dialog" }}
            sx={{ "& .MuiDialog-paper": { overflow: "visible" } }}
        >
            <div className="pf-dialog-header">
                <span className="pf-dialog-header-icon" aria-hidden>
                    <KeyRound size={20} />
                </span>
                <div className="pf-dialog-header-copy">
                    <h2>Change password</h2>
                    <p>Enter your current password, then choose a new one.</p>
                </div>
                <IconButton
                    aria-label="close"
                    className="pf-dialog-close"
                    onClick={handleCloseModal}
                >
                    <MyCloseIcon />
                </IconButton>
            </div>
            <DialogContent className="pf-dialog-body" sx={{ overflow: "visible" }}>
                <form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom> Old password <span className="text-danger ms-1">*</span></Typography>
                            <TextField
                                placeholder="Enter old password"
                                className="mt-0"
                                size="small"
                                name="oldPassword"
                                type={showOldPassword ? "text" : "password"}
                                value={formik.values.oldPassword}
                                onChange={handlePasswordChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.oldPassword)}
                                helperText={formik.errors.oldPassword}
                                fullWidth
                                margin="normal"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment
                                            position="end"
                                            sx={{
                                                gap: "10px",
                                            }}
                                        >
                                            <IconButton
                                                onClick={
                                                    handleToggleOldPasswordVisibility
                                                }
                                                edge="end"
                                            >
                                                {showOldPassword ? (
                                                    <MyVisibilityOffIcon />
                                                ) : (
                                                    <MyVisibilityIcon />
                                                )}
                                            </IconButton>
                                            <HtmlTooltip
                                                title={
                                                    <React.Fragment>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                        >
                                                            <Typography
                                                                color="inherit"
                                                                sx={{
                                                                    fontWeight:
                                                                        "bold",
                                                                    marginRight: 1,
                                                                }}
                                                            >
                                                                {"1. Length: "}
                                                                <Typography color="inherit">
                                                                    {
                                                                        "Password must be at least 8 characters long."
                                                                    }
                                                                </Typography>
                                                            </Typography>
                                                        </Box>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                        >
                                                            <Typography
                                                                color="inherit"
                                                                sx={{
                                                                    fontWeight:
                                                                        "bold",
                                                                }}
                                                            >
                                                                {
                                                                    "2. Complexity: "
                                                                }
                                                                <Typography color="inherit">
                                                                    {
                                                                        "Include a mix of uppercase and lowercase letters, numbers, and special characters (e.g., !, @, #, $)."
                                                                    }
                                                                </Typography>
                                                            </Typography>
                                                        </Box>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                        >
                                                            <Typography
                                                                color="inherit"
                                                                sx={{
                                                                    fontWeight:
                                                                        "bold",
                                                                }}
                                                            >
                                                                {
                                                                    "3. Regular Updates: "
                                                                }
                                                                <Typography color="inherit">
                                                                    {
                                                                        "It's recommended to change your password periodically for added security."
                                                                    }
                                                                </Typography>
                                                            </Typography>
                                                        </Box>
                                                    </React.Fragment>
                                                }
                                            >
                                                <IconButton>
                                                    <MyInfoIcon />
                                                </IconButton>
                                            </HtmlTooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{ position: 'relative' }}>
                            <Typography variant="subtitle2" gutterBottom> New password <span className="text-danger ms-1">*</span></Typography>
                            <TextField
                                placeholder="Enter new password"
                                name="newPassword"
                                className="mt-0"
                                size="small"
                                type={showNewPassword ? "text" : "password"}
                                value={formik.values.newPassword}
                                onChange={handlePasswordChange}
                                onBlur={(e) => {
                                    formik.handleBlur(e);
                                    setNewPasswordFocused(false);
                                }}
                                onFocus={() => setNewPasswordFocused(true)}
                                error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                                helperText={formik.touched.newPassword && formik.errors.newPassword}
                                fullWidth
                                margin="normal"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment
                                            position="end"
                                            sx={{
                                                gap: "10px",
                                            }}
                                        >
                                            <IconButton
                                                onClick={
                                                    handleToggleNewPasswordVisibility
                                                }
                                                edge="end"
                                            >
                                                {showNewPassword ? (
                                                    <MyVisibilityOffIcon />
                                                ) : (
                                                    <MyVisibilityIcon />
                                                )}
                                            </IconButton>
                                            <HtmlTooltip
                                                title={
                                                    <React.Fragment>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                        >
                                                            <Typography
                                                                color="inherit"
                                                                sx={{
                                                                    fontWeight:
                                                                        "bold",
                                                                    marginRight: 1,
                                                                }}
                                                            >
                                                                {"1. Length: "}
                                                                <Typography color="inherit">
                                                                    {
                                                                        "Password must be at least 8 characters long."
                                                                    }
                                                                </Typography>
                                                            </Typography>
                                                        </Box>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                        >
                                                            <Typography
                                                                color="inherit"
                                                                sx={{
                                                                    fontWeight:
                                                                        "bold",
                                                                }}
                                                            >
                                                                {
                                                                    "2. Complexity: "
                                                                }
                                                                <Typography color="inherit">
                                                                    {
                                                                        "Include a mix of uppercase and lowercase letters, numbers, and special characters (e.g., !, @, #, $)."
                                                                    }
                                                                </Typography>
                                                            </Typography>
                                                        </Box>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                        >
                                                            <Typography
                                                                color="inherit"
                                                                sx={{
                                                                    fontWeight:
                                                                        "bold",
                                                                }}
                                                            >
                                                                {
                                                                    "3. Regular Updates: "
                                                                }
                                                                <Typography color="inherit">
                                                                    {
                                                                        "It's recommended to change your password periodically for added security."
                                                                    }
                                                                </Typography>
                                                            </Typography>
                                                        </Box>
                                                    </React.Fragment>
                                                }
                                            >
                                                <IconButton>
                                                    <MyInfoIcon />
                                                </IconButton>
                                            </HtmlTooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {newPasswordFocused && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: '102%',
                                        top: '-2rem',
                                        zIndex: 9999,
                                        width: '300px',
                                    }}
                                >
                                    <PasswordStrengthRules password={formik.values.newPassword} show={true} />
                                </Box>
                            )}
                        </Grid>
                        <Grid item xs={12} sx={{ position: 'relative' }}>
                            <Typography variant="subtitle2" gutterBottom> Confirm new password <span className="text-danger ms-1">*</span></Typography>
                            <TextField
                                placeholder="Enter confirm new password"
                                className="mt-0"
                                size="small"
                                name="confirmNewPassword"
                                type={
                                    showConfirmNewPassword ? "text" : "password"
                                }
                                value={formik.values.confirmNewPassword}
                                onChange={handlePasswordChange}
                                onBlur={(e) => {
                                    formik.handleBlur(e);
                                    setConfirmPasswordFocused(false);
                                }}
                                onFocus={() => setConfirmPasswordFocused(true)}
                                error={formik.touched.confirmNewPassword && Boolean(formik.errors.confirmNewPassword)}
                                helperText={formik.touched.confirmNewPassword && formik.errors.confirmNewPassword}
                                fullWidth
                                margin="normal"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment
                                            position="end"
                                            sx={{
                                                gap: "10px",
                                            }}
                                        >
                                            <IconButton
                                                onClick={
                                                    handleToggleConfirmNewPasswordVisibility
                                                }
                                                edge="end"
                                            >
                                                {showConfirmNewPassword ? (
                                                    <MyVisibilityOffIcon />
                                                ) : (
                                                    <MyVisibilityIcon />
                                                )}
                                            </IconButton>
                                            <HtmlTooltip
                                                title={
                                                    <React.Fragment>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                        >
                                                            <Typography
                                                                color="inherit"
                                                                sx={{
                                                                    fontWeight:
                                                                        "bold",
                                                                    marginRight: 1,
                                                                }}
                                                            >
                                                                {"1. Length: "}
                                                                <Typography color="inherit">
                                                                    {
                                                                        "Password must be at least 8 characters long."
                                                                    }
                                                                </Typography>
                                                            </Typography>
                                                        </Box>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                        >
                                                            <Typography
                                                                color="inherit"
                                                                sx={{
                                                                    fontWeight:
                                                                        "bold",
                                                                }}
                                                            >
                                                                {
                                                                    "2. Complexity: "
                                                                }
                                                                <Typography color="inherit">
                                                                    {
                                                                        "Include a mix of uppercase and lowercase letters, numbers, and special characters (e.g., !, @, #, $)."
                                                                    }
                                                                </Typography>
                                                            </Typography>
                                                        </Box>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                        >
                                                            <Typography
                                                                color="inherit"
                                                                sx={{
                                                                    fontWeight:
                                                                        "bold",
                                                                }}
                                                            >
                                                                {
                                                                    "3. Regular Updates: "
                                                                }
                                                                <Typography color="inherit">
                                                                    {
                                                                        "It's recommended to change your password periodically for added security."
                                                                    }
                                                                </Typography>
                                                            </Typography>
                                                        </Box>
                                                    </React.Fragment>
                                                }
                                            >
                                                <IconButton>
                                                    <MyInfoIcon />
                                                </IconButton>
                                            </HtmlTooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {confirmPasswordFocused && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: '102%',
                                        top: '-2rem',
                                        zIndex: 9999,
                                        width: '300px',
                                    }}
                                >
                                    <PasswordStrengthRules password={formik.values.confirmNewPassword} show={true} />
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                    <div className="pf-dialog-footer">
                        <button
                            type="button"
                            className="pf-btn"
                            onClick={handleCloseModal}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="pf-btn is-primary"
                            disabled={
                                !formik.isValid ||
                                buttonDisable ||
                                loading ||
                                !isPasswordValid(formik.values.newPassword)
                            }
                        >
                            Change password
                            {loading && (
                                <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                />
                            )}
                        </button>
                    </div>
                </form>
            </DialogContent>
            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </Dialog>
    );
};

export default ChangePasswordComponent;
